/**
 * The people an attacker is concentrating on — by name, for the security role.
 *
 * This panel changed shape on 2026-08-20. It used to show a count and refuse to
 * show more, on the reasoning that a monitoring dashboard is the wrong home for
 * "which named employees are being targeted". Timo's decision reversed that, and
 * the reasoning is worth keeping visible because it is what makes the processing
 * defensible: the security team cannot protect the ~142 individually-targeted
 * people without knowing who they are, and this is attack data *about* employees
 * rather than performance or productivity monitoring. Those are materially
 * different propositions, and works-council and GDPR guidance is aimed at the
 * second.
 *
 * Processing names deliberately is not the same as processing them carelessly,
 * so three controls replaced the old refusal, all of them enforced by the
 * backend rather than by this component choosing not to render something:
 *
 *   - **Role-gated**, exactly like the licences board. A non-admin session gets
 *     the same counts with `admin: false`, an empty `people` list and a
 *     `withheld_reason` — never a 403, and never a blank panel. "142 people were
 *     targeted" stays visible to everyone; the names need the role.
 *   - **Bounded retention** on the identity rows, purged on write.
 *   - **Board-only** — nothing identity-level reaches an employee-facing surface.
 *
 * Both states are rendered honestly. Without the role this is the count plus the
 * reason; with it, the most-attacked few at L1 and the full ranked list at L2
 * behind a click (dashboard design rules §1 — a long entity list is an L2 view).
 */

import { Icon, Users } from "@norican/icons";
import {
  Badge,
  Button,
  DataGrid,
  type DataGridColumnDef,
  MiniSparkline,
  Statistic,
  Typography,
} from "@norican/react";

import { DetailDrawer, type Verdict } from "@/components/board";
import type { SecurityTargetedPeople, TargetedPerson, VapTrendPoint } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { TARGETED_ANCHOR } from "./exec-tiles";
import { PanelCard } from "./panel-card";
import { formatCount, formatDayLabel } from "./status";

/** `?people=all` opens the full L2 list. */
const PEOPLE_PARAM = "people";
const PEOPLE_DRAWER_VALUE = "all";

/** How many named people the L1 panel shows before deferring to the L2 list. */
const TOP_N = 5;

interface TargetedPeopleProps {
  /** Counts for every session; names for the security role. */
  payload: SecurityTargetedPeople;
  /** Per-day VAP counts and attack-index totals, oldest first. */
  points: VapTrendPoint[];
  /** Last day the People batch ran, or null if it never has. */
  lastBatchDay: string | null;
}

export function TargetedPeople({ payload, points, lastBatchDay }: TargetedPeopleProps) {
  const drawer = useDrawerParam(PEOPLE_PARAM);
  const attackIndex = points.map((point) => point.total_attack_index);
  const vapCounts = points.map((point) => point.vap_count);
  const rising = isRising(attackIndex);
  const ranked = [...payload.people].sort((a, b) => b.attack_index - a.attack_index);

  const columns: DataGridColumnDef<TargetedPerson>[] = [
    {
      id: "person",
      header: "Person",
      accessor: (row) => row.display_name || row.identity,
    },
    { id: "identity", header: "Mail address", accessor: (row) => row.identity },
    { id: "department", header: "Department", accessor: (row) => row.department || "—" },
    {
      id: "attackIndex",
      header: "Attack index",
      numeric: true,
      accessor: (row) => row.attack_index,
      cell: (row) => <span className="tabular-nums">{formatCount(row.attack_index)}</span>,
    },
    {
      id: "daysRanked",
      header: "Days ranked",
      numeric: true,
      accessor: (row) => row.days_ranked,
      cell: (row) => <span className="tabular-nums">{formatCount(row.days_ranked)}</span>,
    },
    {
      id: "families",
      header: "Attack types",
      accessor: (row) => row.threat_families.join(", ") || "—",
    },
  ];

  return (
    <section id={TARGETED_ANCHOR} className="scroll-mt-6">
      <PanelCard
        title="People individually targeted"
        caption={
          lastBatchDay
            ? `Daily People batch · last run ${formatDayLabel(lastBatchDay)}`
            : "The daily People batch has not run yet"
        }
        verdict={targetedVerdict(payload, rising)}
        note={
          <Typography variant="caption" tone="muted">
            <span className="inline-flex items-center gap-1 align-[-2px]">
              <Icon icon={Users} size="xs" />
            </span>{" "}
            {payload.identity_policy} Names are kept for {payload.retention_days} days and purged on
            every write. What is still not stored is bulk message telemetry — recipients, subjects,
            click IPs and user agents of every message — because identifying who is heavily attacked
            does not require a message-level log of everyone. This refreshes once a day rather than
            every poll: the People pool allows a few dozen requests per 24 hours, which a
            dashboard-rate poll would exhaust before lunch.
          </Typography>
        }
      >
        {payload.snapshot_day === null && points.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            No People API data yet. The batch runs once a day, so this fills in after the first full
            cycle.
          </Typography>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="flex flex-col gap-3">
                <Statistic
                  label="People individually targeted"
                  value={formatCount(payload.vap_count)}
                  tone={payload.vap_count > 0 ? "warning" : "neutral"}
                  size="lg"
                  caption={
                    payload.snapshot_day
                      ? `Most recent snapshot, ${formatDayLabel(payload.snapshot_day)}`
                      : "Proofpoint's Very Attacked People, over the reporting window"
                  }
                />
                {vapCounts.length > 1 && (
                  <MiniSparkline
                    data={vapCounts}
                    tone="warning"
                    endMarker="dot"
                    width={180}
                    height={28}
                    aria-label="Individually-targeted people over time"
                  />
                )}
              </div>

              <div className="flex flex-col gap-3">
                <Statistic
                  label="Total attack index"
                  value={formatCount(payload.total_attack_index)}
                  tone={rising ? "warning" : "neutral"}
                  size="lg"
                  caption={
                    rising
                      ? "Rising — attack pressure on this tenant is increasing"
                      : "Proofpoint's weighted measure of attack pressure"
                  }
                />
                {attackIndex.length > 1 && (
                  <MiniSparkline
                    data={attackIndex}
                    tone={rising ? "danger" : "accent"}
                    endMarker="arrow"
                    width={180}
                    height={28}
                    aria-label="Total attack index over time"
                  />
                )}
              </div>
            </div>

            {/* The count is the same number in both states. Only the names move. */}
            {payload.admin ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Typography variant="label" as="h4">
                    Most attacked, by name
                  </Typography>
                  <Badge tone="info" variant="subtle" size="sm">
                    Visible to the security role
                  </Badge>
                </div>
                {ranked.length === 0 ? (
                  <Typography variant="body-sm" tone="muted">
                    The role is granted, and the last batch returned no individually-targeted
                    person.
                  </Typography>
                ) : (
                  <>
                    <ul className="flex flex-col gap-2">
                      {ranked.slice(0, TOP_N).map((person) => (
                        <li
                          key={person.identity}
                          className="border-border flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md border p-3"
                        >
                          <div className="flex flex-col">
                            <Typography variant="body-sm">
                              {person.display_name || person.identity}
                            </Typography>
                            <Typography variant="caption" tone="muted">
                              {person.identity}
                              {person.department ? ` · ${person.department}` : ""}
                            </Typography>
                          </div>
                          <Typography variant="caption" tone="muted" tabular>
                            Attack index {formatCount(person.attack_index)} ·{" "}
                            {formatCount(person.days_ranked)} day
                            {person.days_ranked === 1 ? "" : "s"} ranked
                          </Typography>
                        </li>
                      ))}
                    </ul>
                    {ranked.length > TOP_N && (
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => drawer.open(PEOPLE_DRAWER_VALUE)}
                        >
                          View all {formatCount(ranked.length)} people
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="border-border flex flex-col gap-1 rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon icon={Users} size="sm" tone="info" />
                  <Typography variant="label" as="span">
                    Names need the security role
                  </Typography>
                </div>
                <Typography variant="body-sm" tone="muted">
                  {payload.withheld_reason ||
                    "The count above is the same number the security team sees. Who these people are is available to the security role only, so that protecting them stays a security action with an owner rather than a general readership."}
                </Typography>
              </div>
            )}

            {payload.errors.map((message) => (
              <Typography key={message} variant="caption" tone="danger">
                {message}
              </Typography>
            ))}
          </div>
        )}
      </PanelCard>

      <DetailDrawer
        open={payload.admin && drawer.value === PEOPLE_DRAWER_VALUE}
        onClose={drawer.close}
        title="Individually targeted people"
        breadcrumbs={[{ label: "Security", to: "/security" }, { label: "Targeted people" }]}
      >
        <DataGrid<TargetedPerson>
          aria-label="Individually targeted people, ranked by attack index"
          data={ranked}
          columns={columns}
          getRowId={(row) => row.identity}
          density="compact"
        />
      </DetailDrawer>
    </section>
  );
}

/** What the targeting means, and whether anyone has to act on it. */
function targetedVerdict(payload: SecurityTargetedPeople, rising: boolean): Verdict {
  if (payload.snapshot_day === null) {
    return {
      label: "No data",
      tone: "neutral",
      text: "The daily People batch has not run, so nobody is counted yet.",
    };
  }
  if (payload.vap_count === 0) {
    return {
      label: "Quiet",
      tone: "neutral",
      text: "Nobody is classed as individually targeted in this window.",
    };
  }
  if (rising) {
    return {
      label: "Watch",
      tone: "warning",
      text: `${formatCount(payload.vap_count)} people are individually targeted and the attack index is rising — training and monitoring coverage for this group is the action.`,
    };
  }
  return {
    label: "Covered",
    tone: "warning",
    text: `${formatCount(payload.vap_count)} people are individually targeted, all inside the training and monitoring programme. No decision needed today.`,
  };
}

/**
 * Whether the series ends higher than the mean of the points before it.
 *
 * Compared against a mean rather than the previous point so one noisy day does
 * not flip the caption back and forth.
 */
function isRising(series: number[]): boolean {
  const latest = series.at(-1);
  if (series.length < 3 || latest === undefined) {
    return false;
  }
  const history = series.slice(0, -1);
  const mean = history.reduce((sum, value) => sum + value, 0) / history.length;
  return latest > mean;
}
